<template>
  <div class="flex flex-col w-full h-full gap-3 overflow-y-auto">
    <!-- Step 1: upload -->
    <template v-if="step === 'upload'">
      <p class="text-sm text-gray-600">
        העלו תמונה או קובץ PDF של רשימת השירותים והמחירים שלכם. נחלץ אוטומטית את השמות, המחירים והתיאורים.
        שירותים שכבר קיימים (לפי שם) לא ישוכפלו.
      </p>

      <!-- Selected files -->
      <div v-if="files.length" class="flex flex-col gap-2">
        <div
          v-for="(file, i) in files"
          :key="i"
          class="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2">
          <i class="pi pi-file text-gray-400" />
          <span class="text-sm text-gray-700 truncate flex-1">{{ file.name }}</span>
          <i class="pi pi-times text-gray-400 cursor-pointer hover:text-red-500" @click="removeFile(i)" />
        </div>
      </div>

      <label
        :for="inputId"
        class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
        <i class="pi pi-cloud-upload text-2xl text-gray-400 mb-2" />
        <span class="text-sm text-gray-500">{{ files.length ? 'הוספת קובץ נוסף' : 'לחצו להעלאת רשימת שירותים' }}</span>
        <span class="text-xs text-gray-400 mt-1">PNG, JPG, WEBP, PDF</span>
        <input
          :id="inputId"
          type="file"
          class="hidden"
          accept="image/png, image/jpeg, image/webp, image/heic, application/pdf"
          multiple
          @change="onFilesSelected" />
      </label>

      <drawer-footer
        approve-label="חילוץ שירותים"
        :approveDisable="files.length === 0"
        :approveLoading="extracting"
        @approve-click="extract"
        @cancel-click="$emit('close')" />
    </template>

    <!-- Step 2: review -->
    <template v-else>
      <p v-if="importable.length" class="text-sm text-gray-600">
        נמצאו {{ extracted.length }} שירותים. בחרו אילו לייבא וערכו במידת הצורך.
      </p>
      <p v-else class="text-sm text-gray-600">
        לא נמצאו שירותים חדשים לייבוא.
      </p>

      <div class="flex flex-col gap-3">
        <div
          v-for="(svc, i) in extracted"
          :key="i"
          class="border rounded-lg p-3 flex flex-col gap-2"
          :class="svc.exists ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-200'">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="svc.selected"
              :binary="true"
              :disabled="svc.exists" />
            <input-text v-model="svc.name" class="flex-1" :disabled="svc.exists" />
          </div>
          <span v-if="svc.exists" class="text-xs text-yellow-600">שירות בשם זה כבר קיים — לא ייובא</span>
          <div class="flex gap-1">
            <button
              v-for="opt of priceTypeOptions"
              :key="opt.value"
              type="button"
              :disabled="svc.exists"
              :class="['flex-1 rounded-lg border py-1.5 px-2 text-xs transition-colors disabled:opacity-50',
                       (svc.priceType ?? PriceTypeEnum.FIXED) === opt.value ? 'border-primary bg-primary-50 text-primary font-medium' : 'border-gray-200 bg-white text-gray-600']"
              @click="svc.priceType = opt.value">
              {{ opt.label }}
            </button>
          </div>
          <div class="flex gap-2 flex-col">
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-xs text-gray-500 mb-1">{{ priceLabel(svc) }}</span>
              <input-number v-model="svc.price" class="w-full" :min="0" :maxFractionDigits="2" locale="he-IL" :disabled="svc.exists" />
            </div>
            <div v-if="svc.priceType === PriceTypeEnum.RANGE" class="flex flex-col flex-1 min-w-0">
              <span class="text-xs text-gray-500 mb-1">מחיר מקסימלי</span>
              <input-number v-model="svc.maxPrice" class="w-full" :min="0" :maxFractionDigits="2" locale="he-IL" :disabled="svc.exists" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-xs text-gray-500 mb-1">משך (דקות)</span>
              <input-number v-model="svc.duration" class="w-full" :min="0" :step="5" :disabled="svc.exists" />
            </div>
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 mb-1">קטגוריה</span>
            <AutoComplete
              v-model="svc.category"
              class="w-full"
              :suggestions="categorySuggestions"
              :complete-on-focus="true"
              :disabled="svc.exists"
              placeholder="למשל: שיער, ציפורניים"
              @complete="onCategoryComplete" />
          </div>
          <div class="flex flex-col">
            <span class="text-xs text-gray-500 mb-1">תיאור</span>
            <Textarea v-model="svc.description" :autoResize="false" rows="2" :disabled="svc.exists" />
          </div>
        </div>
      </div>

      <drawer-footer
        :approve-label="`ייבוא ${selectedCount} שירותים`"
        :approveDisable="selectedCount === 0"
        :approveLoading="importing"
        @approve-click="doImport"
        @cancel-click="$emit('close')" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { IExtractedService, PriceTypeEnum } from '@torim-app/types';
import { computed, ref } from 'vue';
import { useToast } from 'vue-toastification';
import Checkbox from 'primevue/checkbox';
import { useAdminStore } from '@app/stores';
import { ServicesApi } from '@app/api/admin/services-api';
import { trackEvent } from '@app/composables/posthog';
import { generateRandomId } from '@app/utils/strings';

interface ReviewService extends IExtractedService {
  selected: boolean;
  exists: boolean;
}

const emit = defineEmits(['close']);
const toast = useToast();
const adminStore = useAdminStore();

const inputId = generateRandomId(10);
const step = ref<'upload' | 'review'>('upload');
const files = ref<File[]>([]);
const extracted = ref<ReviewService[]>([]);
const extracting = ref(false);
const importing = ref(false);

const importable = computed(() => extracted.value.filter(s => !s.exists));
const selectedCount = computed(() => extracted.value.filter(s => s.selected && !s.exists).length);

const priceTypeOptions: Array<{ value: PriceTypeEnum; label: string }> = [
  { value: PriceTypeEnum.FIXED, label: 'מחיר קבוע' },
  { value: PriceTypeEnum.RANGE, label: 'טווח' },
  { value: PriceTypeEnum.FROM, label: 'החל מ-' },
];
const priceLabel = (svc: ReviewService): string => {
  if (svc.priceType === PriceTypeEnum.RANGE) return 'מחיר מינימלי';
  if (svc.priceType === PriceTypeEnum.FROM) return 'מחיר התחלתי';
  return 'מחיר (ש"ח)';
};

// All categories the user can pick from: existing business categories + ones the AI detected.
const allCategories = computed<string[]>(() => {
  const set = new Set<string>();
  for (const s of adminStore.getRegularServices ?? [])
    if (s.category) set.add(s.category);
  for (const s of extracted.value)
    if (s.category) set.add(s.category.trim());
  return Array.from(set);
});
const categorySuggestions = ref<string[]>([]);
const onCategoryComplete = (event: { query: string }) => {
  const q = (event.query ?? '').trim();
  if (!q) {
    categorySuggestions.value = allCategories.value;
    return;
  }
  const matches = allCategories.value.filter(c => c.toLowerCase().includes(q.toLowerCase()));
  categorySuggestions.value = matches.includes(q) ? matches : [...matches, q];
};

const onFilesSelected = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selected = Array.from(target.files ?? []);
  if (selected.length) files.value = [...files.value, ...selected];
  target.value = '';
};

const removeFile = (index: number) => {
  files.value.splice(index, 1);
};

const extract = async () => {
  if (!files.value.length) return;
  extracting.value = true;
  trackEvent('import_services_extract_clicked', { files: files.value.length });
  try {
    const formData = new FormData();
    for (const file of files.value) formData.append('files', file);
    const result = await ServicesApi.extractServicesFromFiles(formData);

    if (!result.length) {
      toast.info('לא הצלחנו לזהות שירותים בקבצים, נסו קובץ ברור יותר');
      return;
    }

    // Match the backend, which checks ALL services (any type, visible or not) by name.
    const existingNames = new Set(
      [
        ...(adminStore.services ?? []),
        ...(adminStore.complementaryServices ?? []),
        ...(adminStore.privateServices ?? []),
      ].map(s => (s.name ?? '').trim().toLowerCase())
    );
    extracted.value = result.map(s => {
      const exists = existingNames.has((s.name ?? '').trim().toLowerCase());
      return {
        name: s.name,
        price: s.price ?? 0,
        priceType: s.priceType ?? PriceTypeEnum.FIXED,
        maxPrice: s.maxPrice,
        description: s.description ?? '',
        duration: s.duration ?? 30,
        category: s.category ?? '',
        selected: !exists,
        exists,
      };
    });
    step.value = 'review';
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לחלץ את השירותים, אנא נסו שוב');
  } finally {
    extracting.value = false;
  }
};

const doImport = async () => {
  const toImport: IExtractedService[] = extracted.value
    .filter(s => s.selected && !s.exists && s.name?.trim())
    .map(s => {
      const priceType = s.priceType ?? PriceTypeEnum.FIXED;
      const price = s.price ?? 0;
      return {
        name: s.name.trim(),
        price,
        priceType,
        maxPrice: priceType === PriceTypeEnum.RANGE && s.maxPrice && s.maxPrice > price ? s.maxPrice : undefined,
        description: s.description?.trim() || undefined,
        duration: s.duration && s.duration > 0 ? s.duration : undefined,
        category: s.category?.trim() || undefined,
      };
    });

  if (!toImport.length) return;

  importing.value = true;
  trackEvent('import_services_confirmed', { count: toImport.length });
  try {
    const result = await ServicesApi.importServices(toImport);
    await adminStore.fetchBusinessData();
    toast.success(`יובאו ${result.created} שירותים בהצלחה`);
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לייבא את השירותים, אנא נסו שוב');
  } finally {
    importing.value = false;
  }
};
</script>
